import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Table, Tag } from "antd";
import {
  AppstoreOutlined, ShoppingCartOutlined, ShopOutlined, FileTextOutlined,
  TeamOutlined, TagsOutlined, BankOutlined, DollarOutlined, SettingOutlined,
  MoonOutlined, LogoutOutlined, EditOutlined, PrinterOutlined, DeleteOutlined,
  CloseOutlined, RiseOutlined, FallOutlined, SearchOutlined,
} from "@ant-design/icons";

/**
 * Reference demo: a two-tier icon-rail + label sidebar, soft stat cards, and
 * a right-side sliding drawer for row details (replacing a centered modal).
 * Nothing here is wired to a real API — pure layout/interaction reference.
 * Colors follow the validated dataviz palette (categorical slots 1/2/3).
 */

const SERIES_BLUE = "#2a78d6";   /* categorical slot 1 — Gross margin */
const SERIES_ORANGE = "#eb6834"; /* categorical slot 2 — Revenue */
const DONUT_COLORS = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#4a3aa7", "#e34948", "#008300"];

const BAR_DATA = [
  { day: "1 Jul", margin: 33, revenue: 38 },
  { day: "2 Jul", margin: 31, revenue: 44 },
  { day: "3 Jul", margin: 21, revenue: 57 },
  { day: "4 Jul", margin: 33, revenue: 41 },
  { day: "5 Jul", margin: 52, revenue: 45 },
  { day: "6 Jul", margin: 53, revenue: 58 },
  { day: "7 Jul", margin: 21, revenue: 46 },
  { day: "8 Jul", margin: 33, revenue: 46 },
  { day: "9 Jul", margin: 25, revenue: 40 },
  { day: "10 Jul", margin: 47, revenue: 51 },
  { day: "11 Jul", margin: 32, revenue: 43 },
  { day: "12 Jul", margin: 56, revenue: 53 },
];

const CATEGORY_DATA = [
  { label: "Living room", pct: 25 },
  { label: "Kids", pct: 17 },
  { label: "Office", pct: 13 },
  { label: "Bedroom", pct: 12 },
  { label: "Kitchen", pct: 9 },
  { label: "Bathroom", pct: 8 },
  { label: "Dining room", pct: 6 },
  { label: "Decor", pct: 5 },
];

const ORDER_ROWS = [
  { id: "AKF12875", product: "Morpheus", departure: "21 July 2026 · 08:00 PM", delivery: "22 July 2026 · 05:00 PM", status: "in_progress", total: "3,412.00", buyer: "Warm Earth Co.", shipFrom: { country: "France", city: "Paris", zip: "75016", addr: "Stock #11, Av. Henri Martin" }, shipTo: { country: "Italy", city: "Roma", zip: "00146", addr: "Via Enrico Fermi, 71" }, driver: "Drew Cano", truck: "LK 12357" },
  { id: "HGK12987", product: "Vortex, Earth...", departure: "18 July 2026 · 07:00 PM", delivery: "20 July 2026 · 04:00 PM", status: "completed", total: "5,120.40", buyer: "Vortex Traders", shipFrom: { country: "Spain", city: "Madrid", zip: "28001", addr: "Calle Mayor 4" }, shipTo: { country: "Poland", city: "Warsaw", zip: "00-001", addr: "Ul. Marszalkowska 10" }, driver: "Nadia Rocher", truck: "PL 88213" },
  { id: "FTR36158", product: "Measure", departure: "17 July 2026 · 05:00 AM", delivery: "—", status: "in_progress", total: "1,860.00", buyer: "Measure Ltd.", shipFrom: { country: "Germany", city: "Berlin", zip: "10115", addr: "Torstrasse 22" }, shipTo: { country: "Austria", city: "Vienna", zip: "1010", addr: "Stephansplatz 3" }, driver: "Ilan Roth", truck: "DE 40521" },
  { id: "DSD15879", product: "Warm, Earth", departure: "16 July 2026 · 07:00 AM", delivery: "17 July 2026 · 08:00 PM", status: "completed", total: "3,763.20", buyer: "Warm Earth Co.", shipFrom: { country: "France", city: "Paris", zip: "75016", addr: "Stock #11, Av. Henri Martin" }, shipTo: { country: "Italy", city: "Roma", zip: "00146", addr: "Via Enrico Fermi, 71" }, driver: "Drew Cano", truck: "LK 12357" },
  { id: "SDO12669", product: "Wait, Circus", departure: "05 July 2026 · 08:00 PM", delivery: "10 July 2026 · 11:00 PM", status: "returned", total: "980.00", buyer: "Circus Freight", shipFrom: { country: "Romania", city: "Bucharest", zip: "010011", addr: "Calea Victoriei 5" }, shipTo: { country: "Ukraine", city: "Kyiv", zip: "01001", addr: "Khreshchatyk 1" }, driver: "Oksana Bilyk", truck: "UA 77410" },
  { id: "AKK51698", product: "Brace", departure: "01 July 2026 · 11:00 PM", delivery: "07 July 2026 · 05:00 PM", status: "canceled", total: "0.00", buyer: "Brace & Co.", shipFrom: { country: "Italy", city: "Milan", zip: "20121", addr: "Via Dante 2" }, shipTo: { country: "France", city: "Lyon", zip: "69001", addr: "Rue de la République 8" }, driver: "—", truck: "—" },
];

const STATUS_META = {
  in_progress: { label: "In progress", color: "gold" },
  completed: { label: "Completed", color: "green" },
  returned: { label: "Returned", color: "orange" },
  canceled: { label: "Canceled", color: "default" },
};

const NAV_GROUPS = [
  {
    letter: "M",
    label: "Marketing",
    items: [
      { key: "dashboard", icon: <AppstoreOutlined />, label: "Dashboard" },
      { key: "marketplace", icon: <ShoppingCartOutlined />, label: "Marketplace" },
      { key: "orders", icon: <ShopOutlined />, label: "Orders" },
      { key: "tracking", icon: <FileTextOutlined />, label: "Tracking" },
      { key: "customers", icon: <TeamOutlined />, label: "Customers" },
      { key: "discounts", icon: <TagsOutlined />, label: "Discounts" },
    ],
  },
  {
    letter: "P",
    label: "Payments",
    items: [
      { key: "ledger", icon: <BankOutlined />, label: "Ledger" },
      { key: "taxes", icon: <DollarOutlined />, label: "Taxes" },
    ],
  },
  {
    letter: "S",
    label: "System",
    items: [
      { key: "settings", icon: <SettingOutlined />, label: "Settings" },
    ],
  },
];

/* ── SVG bar chart: two series, shared baseline ─────────────────────────── */
function BarChart({ data }) {
  const [hover, setHover] = useState(null);
  const width = 900;
  const height = 260;
  const padLeft = 40;
  const padBottom = 26;
  const max = 70;
  const groupW = (width - padLeft) / data.length;
  const barW = 12;
  const gap = 4;
  const yFor = (v) => height - padBottom - (v / max) * (height - padBottom - 10);

  return (
    <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", height: "auto", display: "block" }} role="img" aria-label="Product sales by day">
        {[0, 10, 20, 30, 40, 50, 60, 70].map((tick) => (
          <g key={tick}>
            <line x1={padLeft} x2={width} y1={yFor(tick)} y2={yFor(tick)} stroke="#e1e0d9" strokeWidth={1} />
            <text x={padLeft - 8} y={yFor(tick) + 4} textAnchor="end" fontSize={10} fill="#898781">{tick}K</text>
          </g>
        ))}
        {data.map((d, i) => {
          const gx = padLeft + i * groupW + groupW / 2;
          const isHover = hover === i;
          return (
            <g key={d.day}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              style={{ cursor: "pointer" }}
            >
              <rect x={gx - barW - gap / 2} y={yFor(d.margin)} width={barW} height={height - padBottom - yFor(d.margin)} rx={3} fill={SERIES_BLUE} opacity={isHover ? 1 : 0.85} />
              <rect x={gx + gap / 2} y={yFor(d.revenue)} width={barW} height={height - padBottom - yFor(d.revenue)} rx={3} fill={SERIES_ORANGE} opacity={isHover ? 1 : 0.85} />
              <rect x={gx - groupW / 2} y={0} width={groupW} height={height - padBottom} fill="transparent" />
              <text x={gx} y={height - 8} textAnchor="middle" fontSize={10} fontWeight={isHover ? 800 : 600} fill={isHover ? "#0b0b0b" : "#898781"}>{d.day}</text>
            </g>
          );
        })}
      </svg>
      {hover != null && (
        <div style={{
          position: "absolute", top: 6, insetInlineStart: `${(hover / data.length) * 100}%`,
          transform: "translateX(-10%)", background: "#fff", border: "1px solid #e1e0d9",
          borderRadius: 10, padding: "8px 12px", boxShadow: "0 4px 14px rgba(11,11,11,0.12)",
          fontSize: 12, whiteSpace: "nowrap", pointerEvents: "none",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: SERIES_BLUE, display: "inline-block" }} />
            Gross margin ${(BAR_DATA[hover].margin * 986.5).toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: SERIES_ORANGE, display: "inline-block" }} />
            Revenue ${(BAR_DATA[hover].revenue * 986.5).toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── SVG donut chart ─────────────────────────────────────────────────────── */
function DonutChart({ data }) {
  const size = 220;
  const r = 80;
  const stroke = 34;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  let acc = 0;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} style={{ width: "100%", maxWidth: 220, height: "auto", display: "block", margin: "0 auto" }} role="img" aria-label="Sales by product category">
      <g transform={`rotate(-90 ${cx} ${cy})`}>
        {data.map((d, i) => {
          const len = (d.pct / 100) * circumference;
          const dasharray = `${len} ${circumference - len}`;
          const dashoffset = -((acc / 100) * circumference);
          acc += d.pct;
          return (
            <circle
              key={d.label}
              cx={cx} cy={cy} r={r} fill="none"
              stroke={DONUT_COLORS[i % DONUT_COLORS.length]}
              strokeWidth={stroke}
              strokeDasharray={dasharray}
              strokeDashoffset={dashoffset}
            />
          );
        })}
      </g>
    </svg>
  );
}

/* ── Side rail (two-tier: icon strip + label panel) ─────────────────────── */
function DashboardSidebar({ collapsed, onToggle, active, onSelect }) {
  return (
    <div style={{ display: "flex", flexShrink: 0, alignSelf: "stretch" }}>
      <div style={{
        width: 68, display: "flex", flexDirection: "column", alignItems: "center",
        padding: "18px 0", background: "#fff", borderInlineEnd: "1px solid #edece7", gap: 4,
        minHeight: "100vh", position: "sticky", top: 0, alignSelf: "flex-start",
      }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: "#e9f7ee", display: "flex", alignItems: "center", justifyContent: "center", color: "#1baf7a", fontSize: 17, marginBottom: 16 }}>
          <ShopOutlined />
        </div>
        {NAV_GROUPS.map((group, gi) => (
          <React.Fragment key={group.letter}>
            {gi > 0 && <div style={{ width: 22, height: 1, background: "#edece7", margin: "8px 0" }} />}
            <div style={{ fontSize: 10, fontWeight: 800, color: "#c3c2b7", marginBottom: 4 }}>{group.letter}</div>
            {group.items.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => onSelect(item.key)}
                title={item.label}
                style={{
                  width: 36, height: 36, borderRadius: 10, border: "none", marginBottom: 4,
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
                  cursor: "pointer", fontFamily: "inherit",
                  background: active === item.key ? "#e9f7ee" : "transparent",
                  color: active === item.key ? "#1baf7a" : "#898781",
                }}
              >
                {item.icon}
              </button>
            ))}
          </React.Fragment>
        ))}
        <div style={{ flex: 1 }} />
        <button type="button" onClick={onToggle} title="Toggle" style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid #edece7", background: "#fff", cursor: "pointer", color: "#898781" }}>
          <SettingOutlined style={{ fontSize: 13 }} />
        </button>
      </div>

      {!collapsed && (
        <div style={{ width: 210, background: "#fbfbfa", borderInlineEnd: "1px solid #edece7", padding: "18px 16px", display: "flex", flexDirection: "column", minHeight: "100vh", position: "sticky", top: 0, alignSelf: "flex-start" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 22 }}>
            <div style={{ width: 26, height: 26, borderRadius: 8, background: "#1baf7a", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13 }}>
              <ShopOutlined />
            </div>
            <span style={{ fontWeight: 800, fontSize: 16, color: "#0b0b0b" }}>Velan</span>
          </div>

          {NAV_GROUPS.map((group) => (
            <div key={group.label} style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", color: "#c3c2b7", marginBottom: 8 }}>
                {group.label.toUpperCase()}
              </div>
              {group.items.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => onSelect(item.key)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, width: "100%",
                    padding: "9px 10px", borderRadius: 9, border: "none", marginBottom: 2,
                    background: active === item.key ? "#1baf7a" : "transparent",
                    color: active === item.key ? "#fff" : "#52514e",
                    fontWeight: active === item.key ? 700 : 600, fontSize: 13.5,
                    cursor: "pointer", textAlign: "start", fontFamily: "inherit",
                  }}
                >
                  {item.icon}
                  {item.label}
                </button>
              ))}
            </div>
          ))}

          <div style={{ flex: 1 }} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 2px" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: "#52514e" }}>
              <MoonOutlined /> Dark mode
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14, paddingTop: 14, borderTop: "1px solid #edece7" }}>
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#e9f7ee", display: "flex", alignItems: "center", justifyContent: "center", color: "#1baf7a", fontWeight: 800, fontSize: 12 }}>HN</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#0b0b0b" }}>Harper Nelson</div>
              <div style={{ fontSize: 11, color: "#898781" }}>Admin Manager</div>
            </div>
            <LogoutOutlined style={{ marginInlineStart: "auto", color: "#898781", fontSize: 13 }} />
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Stat card ────────────────────────────────────────────────────────── */
function StatCard({ icon, label, value, delta, positive }) {
  return (
    <div style={{ flex: 1, minWidth: 170, background: "#fff", border: "1px solid #edece7", borderRadius: 14, padding: "16px 18px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#898781", fontSize: 12.5, fontWeight: 600, marginBottom: 10 }}>
        <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, borderRadius: 7, background: "#f4f3ef" }}>{icon}</span>
        {label}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span style={{ fontSize: 22, fontWeight: 800, color: "#0b0b0b", letterSpacing: "-0.01em" }}>{value}</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 2, fontSize: 12, fontWeight: 700, color: positive ? "#006300" : "#d03b3b" }}>
          {positive ? <RiseOutlined /> : <FallOutlined />} {delta}
        </span>
      </div>
    </div>
  );
}

/* ── Right-side sliding drawer (replaces centered modal for row detail) ─── */
function OrderDrawer({ order, onClose }) {
  const open = Boolean(order);
  const bodyRef = useRef(null);

  // New order selected (or drawer just opened) → always start scrolled to top,
  // so opening from a scrolled-down table row never shows blank/mid-scrolled content.
  useEffect(() => {
    if (open && bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [open, order?.id]);

  // Lock page scroll while the drawer is open — avoids the background list
  // scrolling underneath a fixed-position panel.
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, [open]);

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, background: "rgba(11,11,11,0.28)", zIndex: 60,
          opacity: open ? 1 : 0, pointerEvents: open ? "auto" : "none", transition: "opacity 0.2s ease",
        }}
      />
      <div
        role="dialog"
        aria-hidden={!open}
        style={{
          position: "fixed", top: 0, insetInlineEnd: 0, height: "100vh", width: "min(420px, 100vw)",
          background: "#fff", zIndex: 61, boxShadow: "-8px 0 24px rgba(11,11,11,0.10)",
          transform: open ? "translateX(0)" : "translateX(100%)", transition: "transform 0.22s ease",
          display: "flex", flexDirection: "column",
        }}
      >
        {order && (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 20px", borderBottom: "1px solid #edece7" }}>
              <div>
                <div style={{ fontSize: 17, fontWeight: 800, color: "#0b0b0b" }}>{order.id}</div>
                <div style={{ fontSize: 12, color: "#898781", marginTop: 2 }}>{order.product}</div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button type="button" title="Edit" style={iconBtnStyle}><EditOutlined /></button>
                <button type="button" title="Print" style={iconBtnStyle}><PrinterOutlined /></button>
                <button type="button" title="Delete" style={iconBtnStyle}><DeleteOutlined /></button>
                <button type="button" title="Close" onClick={onClose} style={iconBtnStyle}><CloseOutlined /></button>
              </div>
            </div>

            <div ref={bodyRef} style={{ flex: 1, overflowY: "auto", padding: "18px 20px" }}>
              <div style={{ marginBottom: 18 }}>
                <Tag color={STATUS_META[order.status].color} style={{ borderRadius: 999, fontWeight: 700 }}>{STATUS_META[order.status].label}</Tag>
              </div>

              <DrawerSection title="Shipping address">
                <DrawerRow label="Country" value={order.shipFrom.country} />
                <DrawerRow label="City" value={order.shipFrom.city} />
                <DrawerRow label="ZIP code" value={order.shipFrom.zip} />
                <DrawerRow label="Stock address" value={order.shipFrom.addr} />
              </DrawerSection>

              <DrawerSection title="Delivery address">
                <DrawerRow label="Country" value={order.shipTo.country} />
                <DrawerRow label="City" value={order.shipTo.city} />
                <DrawerRow label="ZIP code" value={order.shipTo.zip} />
                <DrawerRow label="Street" value={order.shipTo.addr} />
              </DrawerSection>

              <DrawerSection title="Driver & truck">
                <DrawerRow label="Driver" value={order.driver} />
                <DrawerRow label="Truck" value={order.truck} />
              </DrawerSection>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderTop: "1px solid #edece7" }}>
              <span style={{ fontSize: 12.5, color: "#898781", fontWeight: 600 }}>Total</span>
              <span style={{ fontSize: 19, fontWeight: 800, color: "#0b0b0b" }}>${order.total}</span>
            </div>
          </>
        )}
      </div>
    </>
  );
}

const iconBtnStyle = {
  width: 30, height: 30, borderRadius: 8, border: "1px solid #edece7", background: "#fff",
  color: "#52514e", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
};

function DrawerSection({ title, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: "#c3c2b7", marginBottom: 8 }}>{title}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 16px" }}>{children}</div>
    </div>
  );
}

function DrawerRow({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "#898781", marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#0b0b0b" }}>{value}</div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   MAIN
───────────────────────────────────────────────────────────────────────── */
export default function DashboardDrawerDemo() {
  useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const [activeNav, setActiveNav] = useState("dashboard");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderSearch, setOrderSearch] = useState("");
  const [orderStatusFilter, setOrderStatusFilter] = useState("all");

  const columns = [
    { title: <input type="checkbox" aria-label="Select all orders" />, key: "sel", width: 40, render: () => <input type="checkbox" aria-label="Select order" onClick={(e) => e.stopPropagation()} /> },
    { title: "Order ID", dataIndex: "id", key: "id", render: (v) => <b style={{ fontFamily: "monospace", fontSize: 12.5 }}>{v}</b> },
    { title: "Product", dataIndex: "product", key: "product" },
    { title: "Buyer", dataIndex: "buyer", key: "buyer", render: (v) => <span style={{ color: "#52514e" }}>{v}</span> },
    { title: "Departure date", dataIndex: "departure", key: "departure", render: (v) => <span style={{ fontSize: 12.5 }}>{v}</span> },
    { title: "Delivery date", dataIndex: "delivery", key: "delivery", render: (v) => <span style={{ fontSize: 12.5 }}>{v}</span> },
    { title: "Status", dataIndex: "status", key: "status", render: (v) => <Tag color={STATUS_META[v].color} style={{ borderRadius: 999, fontWeight: 700, border: "none" }}>{STATUS_META[v].label}</Tag> },
    { title: "Total", dataIndex: "total", key: "total", align: "right", render: (v) => <b>${v}</b> },
  ];

  const statusCounts = ORDER_ROWS.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  const filteredOrders = ORDER_ROWS.filter((r) => {
    const matchesStatus = orderStatusFilter === "all" || r.status === orderStatusFilter;
    const q = orderSearch.trim().toLowerCase();
    const matchesSearch = !q
      || r.id.toLowerCase().includes(q)
      || r.product.toLowerCase().includes(q)
      || r.buyer.toLowerCase().includes(q);
    return matchesStatus && matchesSearch;
  });

  const statusFilterChip = (key, label) => (
    <button
      key={key}
      type="button"
      onClick={() => setOrderStatusFilter(key)}
      style={{
        padding: "7px 14px", borderRadius: 999, border: "1px solid #edece7", cursor: "pointer",
        fontSize: 12.5, fontWeight: 700, fontFamily: "inherit", whiteSpace: "nowrap",
        background: orderStatusFilter === key ? "#1baf7a" : "#fff",
        color: orderStatusFilter === key ? "#fff" : "#52514e",
        borderColor: orderStatusFilter === key ? "#1baf7a" : "#edece7",
      }}
    >
      {label} {key === "all" ? ORDER_ROWS.length : (statusCounts[key] || 0)}
    </button>
  );

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f4f3ef", fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif" }}>
      <DashboardSidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} active={activeNav} onSelect={setActiveNav} />

      <div style={{ flex: 1, minWidth: 0, padding: "22px clamp(16px, 3vw, 32px) 40px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "#0b0b0b" }}>Dashboard</h1>
          <div style={{ fontSize: 12.5, color: "#898781", fontWeight: 600 }}>
            Reference demo — sidebar + stat cards + charts + right-side drawer, no live data
          </div>
        </div>

        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 20 }}>
          <StatCard icon={<TeamOutlined />} label="Total customers" value="567,899" delta="2.5%" positive />
          <StatCard icon={<DollarOutlined />} label="Total revenue" value="$3,465 M" delta="0.5%" positive />
          <StatCard icon={<ShopOutlined />} label="Total orders" value="1,136 M" delta="0.2%" positive={false} />
          <StatCard icon={<FileTextOutlined />} label="Total returns" value="1,789" delta="0.12%" positive />
        </div>

        <div style={{ background: "#fff", border: "1px solid #edece7", borderRadius: 16, padding: "20px 22px", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#0b0b0b" }}>Product sales</div>
            <div style={{ display: "flex", gap: 16, fontSize: 12.5, fontWeight: 700, color: "#52514e" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 9, height: 9, borderRadius: "50%", background: SERIES_BLUE, display: "inline-block" }} />Gross margin</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 9, height: 9, borderRadius: "50%", background: SERIES_ORANGE, display: "inline-block" }} />Revenue</span>
            </div>
          </div>
          <BarChart data={BAR_DATA} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1.2fr) minmax(0,1fr)", gap: 16, marginBottom: 16 }}>
          <div style={{ background: "#fff", border: "1px solid #edece7", borderRadius: 16, padding: "20px 22px" }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#0b0b0b", marginBottom: 16 }}>Sales by product category</div>
            <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
              <div style={{ flex: "0 0 auto", width: 180 }}><DonutChart data={CATEGORY_DATA} /></div>
              <div style={{ flex: 1, minWidth: 200, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 14px" }}>
                {CATEGORY_DATA.map((d, i) => (
                  <div key={d.label} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "#52514e", fontWeight: 600 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: DONUT_COLORS[i], display: "inline-block", flexShrink: 0 }} />
                    {d.label} — <b style={{ color: "#0b0b0b" }}>{d.pct}%</b>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{ background: "#fff", border: "1px solid #edece7", borderRadius: 16, padding: "20px 22px" }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#0b0b0b", marginBottom: 16 }}>Sales by countries</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[["Poland", 19], ["Austria", 15], ["Spain", 13], ["Romania", 12], ["France", 11], ["Italy", 11], ["Germany", 10]].map(([name, pct]) => (
                <div key={name} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#1baf7a", display: "inline-block", flexShrink: 0 }} />
                  <span style={{ flex: 1, color: "#52514e", fontWeight: 600 }}>{name}</span>
                  <b style={{ color: "#0b0b0b" }}>{pct}%</b>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ background: "#fff", border: "1px solid #edece7", borderRadius: 16, overflow: "hidden" }}>
          <div style={{ padding: "16px 22px", borderBottom: "1px solid #edece7" }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#0b0b0b", marginBottom: 14 }}>
              Orders <span style={{ fontWeight: 600, color: "#898781", fontSize: 13 }}>({filteredOrders.length} of {ORDER_ROWS.length})</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {statusFilterChip("all", "All orders")}
                {statusFilterChip("in_progress", "In progress")}
                {statusFilterChip("completed", "Completed")}
                {statusFilterChip("returned", "Returned")}
                {statusFilterChip("canceled", "Canceled")}
              </div>
              <div style={{ position: "relative", marginInlineStart: "auto", minWidth: 220 }}>
                <SearchOutlined style={{ position: "absolute", insetInlineStart: 12, top: "50%", transform: "translateY(-50%)", color: "#898781", fontSize: 13 }} />
                <input
                  type="text"
                  value={orderSearch}
                  onChange={(e) => setOrderSearch(e.target.value)}
                  placeholder="Search order ID, product, buyer…"
                  style={{
                    width: "100%", height: 36, borderRadius: 9, border: "1px solid #edece7",
                    padding: "0 12px 0 34px", fontSize: 13, fontFamily: "inherit", outline: "none",
                    background: "#fbfbfa", color: "#0b0b0b",
                  }}
                />
              </div>
            </div>
          </div>
          <Table
            size="middle"
            dataSource={filteredOrders.map((r) => ({ ...r, key: r.id }))}
            columns={columns}
            pagination={filteredOrders.length > 5 ? { pageSize: 5, size: "small" } : false}
            locale={{ emptyText: "No orders match your search" }}
            onRow={(record) => ({
              onClick: () => setSelectedOrder(record),
              style: { cursor: "pointer" },
            })}
          />
        </div>
      </div>

      <OrderDrawer order={selectedOrder} onClose={() => setSelectedOrder(null)} />
    </div>
  );
}
